// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

import { registerLogoutHook } from '@/lib/logoutHooks';
import { tokenExpiresAtMs } from '@/lib/tokenExpiry';
import { useAuthStore } from '@/store/authStore';
import { useAuth, useClerk, useUser } from '@clerk/clerk-react';
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AuthLoading } from './AuthLoading';
import { AuthSyncContext } from './AuthSyncContext';
import { getLegacyHostRedirect } from './legacyHostRedirect';

const EXCHANGE_TIMEOUT_MS = 15_000;
const RETRY_DELAY_MS = 2000;
const MAX_RETRIES = 1;
// setTimeout cannot wait longer than this; app tokens last 30 days.
const MAX_TIMER_MS = 2 ** 31 - 1;

const errorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout>;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(
      () => reject(new Error(`Token sync timed out after ${ms / 1000}s`)),
      ms
    );
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

/**
 * Keeps the Hanggent token in step with the Clerk session: exchanges it when
 * the user is signed in to Clerk but has no (or an expired) app token, when the
 * token expires while the page is open, and when Clerk switches to another
 * user; logs the app out when the Clerk session ends; and ends the Clerk
 * session when the app logs out. Must sit inside ClerkProvider.
 */
export function AuthSyncProvider({ children }: { children: ReactNode }) {
  const { isSignedIn, isLoaded: authLoaded, getToken } = useAuth();
  const { user, isLoaded: userLoaded } = useUser();
  const clerk = useClerk();
  const token = useAuthStore((state) => state.token);
  const tokenExpired = useAuthStore((state) => state.isTokenExpired());
  const clerkLogin = useAuthStore((state) => state.clerkLogin);

  const [syncing, setSyncing] = useState(false);
  const [failed, setFailed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Bumped to wake the sync effect up after a sign-out attempt failed.
  const [resumeTick, setResumeTick] = useState(0);
  // Bumped when the current app token reaches its expiry.
  const [expiryTick, setExpiryTick] = useState(0);

  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);
  // Set synchronously when the app logs out: Clerk still reports a session
  // until sign-out completes and the sync must not log the user back in.
  const loggingOutRef = useRef(false);
  // The Clerk session this mount has seen, to notice it ending or changing hands.
  const wasSignedInRef = useRef(false);
  const clerkUserIdRef = useRef<string | null>(null);
  // The app token belongs to the previous Clerk user: fetch a new one even if it is valid.
  const forceExchangeRef = useRef(false);
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;
  const signOutRef = useRef(clerk.signOut);
  signOutRef.current = clerk.signOut;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const target = getLegacyHostRedirect(window.location.href);
    if (target) window.location.replace(target);
  }, []);

  useEffect(
    () =>
      registerLogoutHook(async () => {
        loggingOutRef.current = true;
        try {
          await signOutRef.current();
        } catch (signOutError) {
          console.warn('[AuthSyncProvider] Clerk sign-out failed:', signOutError);
          loggingOutRef.current = false;
          if (mountedRef.current) setResumeTick((tick) => tick + 1);
        }
      }),
    []
  );

  // Wake up when the current token expires, so a long-open page renews it.
  useEffect(() => {
    const expiresAt = tokenExpiresAtMs(token);
    if (expiresAt === null || expiresAt <= Date.now()) return;
    const timer = setTimeout(
      () => setExpiryTick((tick) => tick + 1),
      Math.min(expiresAt - Date.now() + 50, MAX_TIMER_MS)
    );
    return () => clearTimeout(timer);
  }, [token, expiryTick]);

  useEffect(() => {
    if (!authLoaded || !userLoaded) return;

    if (!isSignedIn || !user) {
      // The Clerk session ended. If the app did not cause that (another tab,
      // a revoked session) the app token must not outlive it.
      const sessionEndedElsewhere =
        wasSignedInRef.current && !loggingOutRef.current;
      wasSignedInRef.current = false;
      clerkUserIdRef.current = null;
      forceExchangeRef.current = false;
      setFailed(false);
      setError(null);
      if (sessionEndedElsewhere) useAuthStore.getState().logout();
      loggingOutRef.current = false;
      return;
    }

    wasSignedInRef.current = true;
    if (clerkUserIdRef.current && clerkUserIdRef.current !== user.id) {
      forceExchangeRef.current = true;
      setFailed(false);
      setError(null);
    }
    clerkUserIdRef.current = user.id;

    if (loggingOutRef.current || failed || inFlightRef.current) return;
    const state = useAuthStore.getState();
    if (!forceExchangeRef.current && state.token && !state.isTokenExpired()) {
      return;
    }
    forceExchangeRef.current = false;

    inFlightRef.current = true;
    setSyncing(true);

    const fail = (message: string) => {
      if (!mountedRef.current) return;
      setError(message);
      setFailed(true);
    };

    void (async () => {
      let lastError: unknown;
      try {
        for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
          try {
            const clerkToken = await getTokenRef.current();
            if (!clerkToken) {
              fail('No Clerk session token available');
              return;
            }
            await withTimeout(clerkLogin(clerkToken), EXCHANGE_TIMEOUT_MS);
            // A token the client already considers expired would loop forever.
            if (useAuthStore.getState().isTokenExpired()) {
              throw new Error('Server returned an expired token');
            }
            return;
          } catch (exchangeError) {
            lastError = exchangeError;
            if (attempt < MAX_RETRIES) {
              console.warn(
                `[AuthSyncProvider] Token sync attempt ${attempt + 1} failed, retrying...`,
                exchangeError
              );
              await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
            }
          }
        }
        console.error('[AuthSyncProvider] Token sync failed:', lastError);
        fail(errorMessage(lastError));
      } finally {
        inFlightRef.current = false;
        if (mountedRef.current) setSyncing(false);
      }
    })();
  }, [
    authLoaded,
    userLoaded,
    isSignedIn,
    user,
    token,
    tokenExpired,
    expiryTick,
    failed,
    resumeTick,
    clerkLogin,
  ]);

  const retry = useCallback(() => {
    setFailed(false);
    setError(null);
  }, []);
  const context = useMemo(() => ({ failed, error, retry }), [failed, error, retry]);

  if (!authLoaded || !userLoaded) {
    return <AuthLoading message="Initializing..." />;
  }
  // Only block the app while there is no token at all. Renewing an expired
  // token happens behind the running app instead of tearing it down.
  const waitingForFirstToken =
    isSignedIn && !!user && !loggingOutRef.current && !token;
  if (!token && (syncing || (waitingForFirstToken && !failed))) {
    return <AuthLoading message="Authenticating..." />;
  }

  return (
    <AuthSyncContext.Provider value={context}>
      {children}
    </AuthSyncContext.Provider>
  );
}
